/*
 * The environment's "where this actually lives" link, and its verdict.
 *
 * This badge used to say Live wherever a URL was configured, which made it a
 * restatement of the config file rather than information. It now reports what
 * a server-side probe found: green and pulsing only when something answered
 * 2xx, amber when it answered but gated, red when it did not answer at all.
 * A URL with no probe yet keeps the old neutral look rather than claiming
 * either way.
 */
import { ExternalLink } from './Icons'
import { hostname } from '../lib/format'
import type { EnvironmentHealth } from '../types'

type Tone = 'live' | 'checking' | 'warn' | 'down'

const TONE: Record<string, { tone: Tone; label: string }> = {
  up: { tone: 'live', label: 'Live' },
  protected: { tone: 'warn', label: 'Auth' },
  error: { tone: 'down', label: 'Error' },
  down: { tone: 'down', label: 'Down' },
  unreachable: { tone: 'down', label: 'Down' },
  blocked: { tone: 'warn', label: 'Blocked' }
}

export function LiveBadge({ url, health, checking }: { url: string; health?: EnvironmentHealth; checking?: boolean }) {
  const resolved = health ? TONE[health.status] : null
  const tone: Tone = resolved?.tone ?? (checking ? 'checking' : 'checking')
  const label = resolved?.label ?? 'Live'

  const title = health
    ? [
        `${hostname(url)}`,
        health.error ?? `HTTP ${health.httpStatus}`,
        health.ms !== null ? `${health.ms}ms` : null,
        health.finalUrl ? `redirected to ${health.finalUrl}` : null
      ]
        .filter(Boolean)
        .join(' · ')
    : `Open ${hostname(url)}${checking ? ' · checking…' : ''}`

  return (
    <a className={`live-badge live-${tone}`} href={url} target="_blank" rel="noreferrer noopener" title={title}>
      <span className="live-badge-dot" aria-hidden="true" />
      {label}
      {health?.httpStatus && tone !== 'live' ? <span className="live-code">{health.httpStatus}</span> : null}
      <ExternalLink size={10} />
    </a>
  )
}
